import { HTTP_INTERCEPTORS } from '@angular/common/http';

import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication
} from '@azure/msal-browser';
import { environment } from '@environment/environment';

import { loggerCallback } from './logger-callback';

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      knownAuthorities: environment.msalConfig.auth.knownAuthorities,
      redirectUri: environment.msalConfig.auth.redirectUri,
      postLogoutRedirectUri: environment.msalConfig.auth.postLogoutRedirectUri
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      // allowNativeBroker: false,
      loggerOptions: {
        loggerCallback,
        logLevel: environment.msalConfig.logLevel,
        piiLoggingEnabled: false
      }
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();

  /*
   * `@azure/msal-angular` v6 matches the keys of the `protectedResourceMap`
   * strictly: the pattern is anchored (`^...$`) and wildcards have to be
   * spelled out. Up to v4 a base URI implicitly covered all of its sub-paths,
   * which is why `http://localhost:8080/backend` used to protect
   * `http://localhost:8080/backend/userInfo` as well.
   *
   * With strict matching that is no longer the case - the interceptor finds no
   * scopes for the endpoint, skips token acquisition and the request is sent
   * without an `Authorization` header, which the backend answers with 401.
   *
   * Every resource is therefore registered twice: once for the base URI itself
   * and once with a `/*` wildcard covering everything below it.
   */
  Object.values(environment.apiConfig).forEach((entry) => {
    const baseUri = entry.uri.replace(/\/$/, '');

    protectedResourceMap.set(baseUri, entry.scopes);
    protectedResourceMap.set(`${baseUri}/*`, entry.scopes);
  });

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
    // Explicit, so the v6 default is a conscious decision and MSAL stops
    // warning about it on every start.
    strictMatching: true
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    loginFailedRoute: environment.msalConfig.loginFailedRoute
  };
}

export const MSAL_PROVIDERS = [
  {
    provide: HTTP_INTERCEPTORS,
    useClass: MsalInterceptor,
    multi: true
  },
  MsalService,
  MsalGuard,
  MsalBroadcastService
];

export const MSAL_TEST_PROVIDERS = [
  {
    provide: MSAL_INSTANCE,
    useFactory: MSALInstanceFactory
  },
  {
    provide: MSAL_GUARD_CONFIG,
    useFactory: MSALGuardConfigFactory
  },
  {
    provide: MSAL_INTERCEPTOR_CONFIG,
    useFactory: MSALInterceptorConfigFactory
  }
];
